import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import { AttributeContainer, AttributeName, AttributeValue, CrossContainer, HeaderContainer, Image, ImageColumn, ImageSection, InputsContainer, SubtitleContainer } from './styles';
import { CircularProgress, Divider, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import theme from '../../theme/theme';
import { AddCircle, Close, Edit } from '@mui/icons-material';
import { UploadFile } from '../../components/UploadFile';
import { postCategory, processCategoryImage, postCategoryImage } from '../../services/categories/postCategories';
import { RequestModal } from '../../components/RequestModal';
import { CategoryActionsProps } from './types';
import { putCategory, putCategoryImage } from '../../services/categories/putCategory';
import { CustomText } from '../../components/CustomText';

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)', 
  width: '70%',
  minWidth: '380px',
  maxHeight: '700px',
  overflow: 'auto',
  borderRadius: '20px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
};

const AddCategory: React.FC<CategoryActionsProps> = ({ action, categoryObj, onResponse }) => {
  // ------------------- JWT -------------------
  const jwt = localStorage.getItem('jwt');

  const imageSpecs = `
  Esta es la imagen actual de la categoría. Si desea cambiarla, cargue una nueva imagen del lado derecho.
  `

  // ------ Modal ------
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setUploadFile(null);
    setIsPosted(false);
    if (action !== 'edit') {
      setCategory('');
    }
  }

  // ------ States ------
  const [category, setCategory] = React.useState(action === 'create' ? '' : categoryObj.name);
  const [uploadFile, setUploadFile] = React.useState<any>(null);
  const [verified, setVerified] = React.useState(false);
  const [isPosted, setIsPosted] = React.useState(false);
  const [invalid, setInvalid] = React.useState(false);

  const [warningTitle, setWarningTitle] = React.useState('');
  const [warningDescription, setWarningDescription] = React.useState('');

  const [loadingCreateCategory, setLoadingCreateCategory] = React.useState(false);

  const [nameError, setNameError] = React.useState(false);

  // ------ Requests ------
  const handleCategory = async () => {
    if (!category && action === 'create') {
      setWarningTitle("Campos incompletos");
      setWarningDescription("Debe completar todos los campos. Por favor, inténtelo de nuevo.");
      setInvalid(true);
    } else if (!uploadFile && action === 'create') {
      setWarningTitle("Campos incompletos");
      setWarningDescription("Debe completar todos los campos. Por favor, inténtelo de nuevo.");
      setInvalid(true);
    } else {
      setLoadingCreateCategory(true);
      if (action === 'create') {
        try {
          const response = await postCategory(jwt!, category);

          const image = await processCategoryImage(jwt!, category, uploadFile);
          setIsPosted(true);
          setWarningTitle('¡Categoría Creada!');
          setWarningDescription('La categoría ha sido creada exitosamente.');
          onResponse && onResponse();
        } catch (err) {
          console.log(err);
          // toast.error("Hubo un error al crear la categoría.");
          setWarningTitle('Ocurrió un error');
          setWarningDescription('Hubo un error al crear la categoría.');
        }
        setLoadingCreateCategory(false);
      } else {
        try {
          if (category) {
            const response = await putCategory(jwt!, categoryObj.id, category);
          }
          if (uploadFile) {
            const response = await putCategoryImage(jwt!, categoryObj.id, uploadFile);
          }
          setIsPosted(true);
          setWarningTitle('¡Categoría Editada!');
          setWarningDescription('La categoría ha sido editada exitosamente.');
          onResponse && onResponse();
        } catch (err) {
          console.log(err);
          // toast.error("Hubo un error al crear la categoría.");
          setWarningTitle('Ocurrió un error');
          setWarningDescription('Hubo un error al editar la categoría.');
        }
        setLoadingCreateCategory(false);
      }
    }
  }

  // ------ Handlers ------
  const handleCategoryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCategory(event.target.value);
  };

  const handleNameBlur = () => {
    if (!category) {
      setNameError(true);
    } else {
      setNameError(false);
    }
  }

  // ------ Effects ------

  return (
    <div>
      {
        isPosted ? (
          <RequestModal
            title={warningTitle}
            description={warningDescription}
            onConfirm={handleClose}
          />
        ) : (
          invalid &&
          <RequestModal
            title='Ocurrió un error'
            description={warningDescription}
            onConfirm={(!category || !uploadFile && action != 'create') ?
              () => { setInvalid(false) } : handleClose}
          />
        )
      }
      <Button onClick={handleOpen} style={{ padding: '0px' }} >
        {action === 'create' ?
          <AddCircle sx={{ color: theme.color.navy, fontSize: '2rem' }} />
          :
          <Edit sx={{ color: theme.color.navy, fontSize: '2rem' }} />
        }
      </Button>
      <Modal
        open={open}
        // onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          {
            loadingCreateCategory ?
              (<div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress sx={{ color: theme.color.navy }} />
              </div>
              ) : (
                <>
                  <CrossContainer
                    onClick={handleClose}
                  >
                    <Close />
                  </CrossContainer>
                  <HeaderContainer>
                    {
                      action === 'create' ?
                        "Agregar nueva categoría"
                        :
                        "Editar categoría"
                    }
                  </HeaderContainer>
                  <InputsContainer>
                    <AttributeContainer>
                      <TextField
                        label="Nombre de categoría"
                        type="text"
                        variant="outlined"
                        fullWidth
                        required
                        sx={{ margin: "15px 0", width: '100%' }}
                        InputProps={{
                          style: {
                            borderRadius: "14px",
                          },
                        }}
                        value={category}
                        onChange={handleCategoryChange}
                        onBlur={handleNameBlur}
                        error={nameError}
                        helperText={nameError ? 'Este campo es obligatorio' : ''}
                      />
                    </AttributeContainer>
                  </InputsContainer>
                  <ImageSection>
                    {
                      categoryObj?.image &&
                      <>
                        <ImageColumn>
                          <SubtitleContainer>Imagen actual</SubtitleContainer>
                          <Divider />
                          <Image src={categoryObj?.image} alt="Imagen de categoría" />
                          <CustomText
                            color={theme.color.navy}
                            content={imageSpecs}
                          />
                        </ImageColumn>
                      </>
                    }
                    <UploadFile
                      status={2}
                      changeFile={setUploadFile}
                      uploadType="imgs"
                      verified={verified}
                      setVerified={setVerified}
                    />
                  </ImageSection>
                  <Button
                    variant="contained"
                    onClick={handleCategory}
                    sx={{
                      width: "100%",
                      marginTop: "20px",
                      borderRadius: "15px",
                      fontSize: "16px",
                      backgroundColor: theme.color.navy,
                      color: theme.color.white,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: theme.color.space,
                      },
                    }}
                  >
                    Guardar
                  </Button>
                </>
              )
          }
        </Box>
      </Modal>
    </div >
  );
}

export default AddCategory;